import { z } from "zod";
import { AI_AGENT_READINESS_SUBCRITERIA } from "./ai-agent-readiness";
import { BRAND_TRUST_CONSISTENCY_SUBCRITERIA } from "./brand-trust-consistency";
import { CONTENT_DESIGN_ORIENTATION_SUBCRITERIA } from "./content-design-orientation";
import { HUMAN_FIRST_CONTENT_QUALITY_SUBCRITERIA } from "./human-first-content-quality";
import { JOURNEY_USABILITY_ACTION_SUBCRITERIA } from "./journey-usability-action";

export type { ContentFitnessSubcriterion } from "./types";

/**
 * The canonical Content Fitness subcriteria in dimension order. Seeded into
 * `public.content_fitness_subcriteria` on backend boot; the array order sets
 * each row's `position`, so append new items within a dimension file rather
 * than reordering.
 */
export const CONTENT_FITNESS_SUBCRITERIA = [
  ...HUMAN_FIRST_CONTENT_QUALITY_SUBCRITERIA,
  ...CONTENT_DESIGN_ORIENTATION_SUBCRITERIA,
  ...AI_AGENT_READINESS_SUBCRITERIA,
  ...JOURNEY_USABILITY_ACTION_SUBCRITERIA,
  ...BRAND_TRUST_CONSISTENCY_SUBCRITERIA,
] as const;

export type ContentFitnessSubcriterionKey =
  (typeof CONTENT_FITNESS_SUBCRITERIA)[number]["key"];

export const ContentFitnessSubcriterionKeySchema = z.enum(
  CONTENT_FITNESS_SUBCRITERIA.map((item) => item.key) as [
    ContentFitnessSubcriterionKey,
    ...ContentFitnessSubcriterionKey[],
  ],
);

export const CONTENT_FITNESS_PILOT_SUBCRITERIA =
  CONTENT_FITNESS_SUBCRITERIA.filter((item) => item.pilot);

/**
 * Named members for the subcriteria business code singles out. Only the ones
 * with a caller are listed: a member here is a claim that some surface behaves
 * differently for that subcriterion, and an unused one would be noise.
 *
 * `satisfies Record<string, ContentFitnessSubcriterionKey>` keeps this honest —
 * a member that does not resolve against the catalog fails to compile.
 */
export const CONTENT_FITNESS_SUBCRITERION_KEY = {
  ACTION_PATH_FRICTION_PROOF:
    "journey_usability_action.action_path_friction_proof",
  HEADING_HIERARCHY: "content_design_orientation.heading_hierarchy",
} as const satisfies Record<string, ContentFitnessSubcriterionKey>;
